<template>
  <div class="gs-aff-panel">
    <header class="gs-aff-panel__head">
      <div>
        <h3>Default commission rules</h3>
        <p>The standard payout for each price point. A price point can inherit this rule or use an override.</p>
      </div>
    </header>

    <div v-if="localModel.pricing_points.length > 6" class="gs-aff-panel__meta">
      <label class="gs-aff-search">
        <gs-icon name="search" size="16"></gs-icon>
        <input v-model="search" type="search" placeholder="Search price points" aria-label="Search price points" />
      </label>
      <span class="gs-aff-count">{{ visiblePricePoints.length }} of {{ localModel.pricing_points.length }} price points</span>
    </div>

    <div class="gs-aff-list">
      <article
        v-for="{ pricePoint, index } in visiblePricePoints"
        :key="pricePoint.id || index"
        class="gs-aff-row is-configured"
        :class="{ 'is-open': openIndex === index }"
      >
        <button
          type="button"
          class="gs-aff-row__head"
          :aria-expanded="openIndex === index ? 'true' : 'false'"
          @click="toggle(index)"
        >
          <span class="gs-aff-row__dot" aria-hidden="true"></span>
          <span class="gs-aff-row__title">
            <span class="gs-aff-row__name">{{ pricePoint.name }}</span>
            <span class="gs-aff-row__summary">{{ commissionSummary(pricePoint) }}</span>
          </span>
          <span class="gs-aff-row__meta">
            <span class="gs-aff-pill">{{ billingLabel(pricePoint) }}</span>
          </span>
          <span class="gs-aff-row__chevron" aria-hidden="true">
            <gs-icon :name="openIndex === index ? 'chevron-up' : 'chevron-down'" size="18"></gs-icon>
          </span>
        </button>

        <div v-if="openIndex === index" class="gs-aff-row__body">
          <template v-for="(commissionTier, commissionTierIndex) in standardCommissions(pricePoint)">
            <section :key="`first-${commissionTier.id || commissionTierIndex}`" class="gs-aff-section">
              <div class="gs-aff-section__head">
                <div>
                  <strong>{{ pricePoint.is_trial == 1 ? 'Initial charge or trial conversion' : 'First payment' }}</strong>
                  <p>What affiliates earn when the buyer makes the first billable payment.</p>
                </div>
                <span v-if="pricePoint.type != 1" class="gs-aff-switch-label">
                  Payment schedule
                  <button
                    type="button"
                    role="switch"
                    class="gs-aff-switch"
                    :aria-checked="isComplexCommission(pricePoint, commissionTier, commissionTierIndex) ? 'true' : 'false'"
                    aria-label="Use a payment-by-payment commission schedule"
                    @click="toggleComplexCommission(pricePoint, commissionTier, commissionTierIndex)"
                  ></button>
                </span>
              </div>
              <div class="gs-aff-grid">
                <label class="gs-aff-field">
                  <span class="gs-aff-field__label">Tier 1 affiliate</span>
                  <span class="gs-aff-input-pair">
                    <input v-model="primaryCommission(pricePoint, commissionTier).commission" class="gs-aff-input" type="number" min="0" aria-label="Tier 1 commission" />
                    <select v-model="primaryCommission(pricePoint, commissionTier).commission_type" class="gs-aff-select" aria-label="Tier 1 commission type">
                      <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                  </span>
                </label>
                <label class="gs-aff-field">
                  <span class="gs-aff-field__label">Tier 2 (optional)</span>
                  <span class="gs-aff-input-pair">
                    <input v-model="primaryCommission(pricePoint, commissionTier).jv_broker_commission" class="gs-aff-input" type="number" min="0" aria-label="Tier 2 commission" />
                    <select v-model="primaryCommission(pricePoint, commissionTier).jv_broker_commission_type" class="gs-aff-select" aria-label="Tier 2 commission type">
                      <option v-for="option in commissionTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                    </select>
                  </span>
                </label>
              </div>
            </section>

            <recurring-schedule
              v-if="pricePoint.type != 1 && isComplexCommission(pricePoint, commissionTier, commissionTierIndex)"
              :key="`schedule-${commissionTier.id || commissionTierIndex}`"
              :commission-tier="commissionTier"
              :commission-type-options="commissionTypeOptions"
            />
          </template>
        </div>
      </article>

      <div v-if="!visiblePricePoints.length" class="gs-aff-empty">
        <strong>No matching price points</strong>
        <p>Try a different search.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { billingLabel } from '../../../pricePointSummary';
import RecurringSchedule from './RecurringSchedule';
import { commissionSummary, primaryCommission, commissionTypeOptions } from './commissionFormat';

export default {
  components: {
    RecurringSchedule,
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    validation: {
      required: true
    }
  },
  data() {
    return {
      localModel: this.model,
      complexCommissionKeys: {},
      openIndex: 0,
      search: '',
    };
  },
  computed: {
    commissionTypeOptions() {
      return commissionTypeOptions(this.$t.bind(this));
    },
    visiblePricePoints() {
      const term = this.search.trim().toLowerCase();
      return this.localModel.pricing_points
        .map((pricePoint, index) => ({ pricePoint, index }))
        .filter(({ pricePoint }) => !term || String(pricePoint.name || '').toLowerCase().includes(term));
    },
  },
  watch: {
    model: {
      handler() {
        this.$emit('update-model', this.localModel);
      },
      deep: true
    }
  },
  methods: {
    billingLabel,
    primaryCommission,
    toggle(index) {
      this.openIndex = this.openIndex === index ? null : index;
    },
    standardCommissions(pricePoint) {
      return (pricePoint.commission_details || []).filter((obj) => obj.type == 1);
    },
    commissionKey(pricePoint, commissionTierIndex) {
      return `${pricePoint.id || pricePoint.name}_${commissionTierIndex}`;
    },
    isComplexCommission(pricePoint, commissionTier, commissionTierIndex) {
      const key = this.commissionKey(pricePoint, commissionTierIndex);
      if (Object.prototype.hasOwnProperty.call(this.complexCommissionKeys, key)) {
        return this.complexCommissionKeys[key];
      }
      // A saved multi-row schedule is already in use, so show it open.
      return (commissionTier.rebill_commissions || []).length > 1;
    },
    toggleComplexCommission(pricePoint, commissionTier, commissionTierIndex) {
      const key = this.commissionKey(pricePoint, commissionTierIndex);
      this.$set(this.complexCommissionKeys, key, !this.isComplexCommission(pricePoint, commissionTier, commissionTierIndex));
    },
    commissionSummary(pricePoint) {
      const tier = this.standardCommissions(pricePoint)[0];
      return tier ? commissionSummary(pricePoint, tier) : 'No default commission';
    },
  },
};
</script>
